import {rootContentInstance} from '@player/src/content/player_content.js';
import {getSettings,getDefaultSettings} from '@player/src/settings/settings';
import {getLangDisplayList} from '@player/src/displayMixins.js';
import {typeAffirmer} from '@player/src/mixins';
import VideoController from './videoTag';
import Hooks from '@player/src/hooks';
import {getErrorDetails} from "@player/src/errors/errorDetails.js";
import browserData from '@player/src/browserSupport.js';

/**
 * @class @function Mainplayer:
 * 
 * @property {Object} _settings:<userinput>
 * @property {HTMLElement} _videoTag:<userinput>
 * @property {Object} _content:<userinput>
 * @property {Object} _basePlayer:<required>
 * @property {Object} _playbackActive<required>
 * @property {Instance of Hook} hub:<required><useraccessible>
 * @property {Instance of VideoController} videoController:<required><useraccessible>
 * @property {Boolean} active:<useraccessible>
 * 
 * ---------------------------------
 * @constructor: @param {Object} { @property {Object} settings }
 * - create hub for event management.
 * - create settings with user settings or default settings.
 * - create instance of VideoController for video tag related methods and listeners.
 * 
 * ---------------------------------
 * @user_accessible @methods :
 * 
 * @method instantiate: @param {HTMLElement} videoTag
 * - to be called once videoTag element is available to instantiate videoController.
 * 
 * 
 * @method loadContent @async: @param content
 * - called by user when new content has to be loaded. 
 * - @call @method _basePlayer.load.
 * - unload previous content.
 * 
 * @method getPlayerSettings: 
 * -get player settings.
 * 
 * @method unloadContent:
 * - unload a content from the player.
 * - @call @method _basePlayer.unload.
 * 
 * @method destroy:
 * - destroy current context by removing all listeners. 
 * - @call @method _basePlayer.destroy.
 * 
 * -------------------------------------------------
 * 
 * @internal @methods :
 * 
 * @method _instantiatePlayer @async: @param content
 * - internal method called inside @method loadContent to determine 
 *   player type to be used for the new content.
 * - assigns hub of new _basePlayer to main hub to listen to _basePlayer emits.
 * - @call @method _basePlayer.instantiate.
 * 
 * @method _determinePlayerType:
 * - internal method called inside @method _instantiatePlayer to get 
 *   import for base player on the basis of playerType.
 * 
 */

const Players = {
    dashPlayer: 'dashPlayer',
    html5Player: 'html5Player',
    hlsPlayer: 'hlsPlayer'
} 
 

 
class Mainplayer{

    constructor({settings} = {}){
        this.hub=new Hooks();
        this._settings=settings?getSettings(settings):getDefaultSettings();
        this.videoController=new VideoController(this._settings);
        this._content=null;
        this._videoTag=null;
        this._basePlayer=null;
        this.active=false;
        this._playbackActive=false;
    }
    
    instantiate(videoTag){
        if( typeAffirmer('element')(this._videoTag) && this.active ){
            this.hub.dispatch('error', getErrorDetails( 0 ) );
            throw { errorDispatched: true }
        }

        let videotagValid= typeAffirmer('element')(videoTag) && ( videoTag.tagName==='VIDEO' );
       
        if(!videotagValid){
           this.hub.dispatch('error',getErrorDetails( 0 ) );
           throw { errorDispatched: true }
        }
        this._videoTag=videoTag;
    
        this.videoController.setVideoTag(this._videoTag);

        this.active=true;
    }

    _determinePlayerType(content,extension){
        if( content.live )return Players.hlsPlayer;
        if( browserData.canPlay( this._videoTag, extension ) ) return Players.html5Player;
        else return Players.dashPlayer;
    }

    async _instantiatePlayer(content){
        //resolve playerType...
        let source = content.getSource(),playerType;
        let extension = (/\.(?<ext>[a-zA-Z\d]+)$/).exec(source)?.groups.ext;
        playerType = this._determinePlayerType( content , extension );
        
        /** clean previous basePlayer instance...*/
        if(this._basePlayer){
            if(this._basePlayer.playerType===playerType){
                //same player continue playback...
                return null;
            }
            await this._basePlayer.destroy();// awaiting could be avoided to improve performance;
        }
        
        let playerFactory=await this._playerCreatorMap(playerType)();
        this._basePlayer=new playerFactory.default(
            {   
                videoTag:this._videoTag,
                settings:this._settings
            });
        this._basePlayer.hub?.attach(this.hub);
        return this._basePlayer.instantiate();
        
    }

    async loadContent(content){
        try{
            if( !( content instanceof rootContentInstance ) ) throw { code: 1 }
                
            if(this._playbackActive)await this.unloadContent();
            
            await this._instantiatePlayer(content);
        
            this._content=content;
            
            let res=await this._basePlayer.load(this._content);

            this._loadSubtitles();

            this._playbackActive=true;

            return res;
            
        }
        catch(err){
            if(!err?.errorDispatched)this.hub.dispatch('error',getErrorDetails( err?.code ) );

            throw { errorDispatched: true } ;
        }
    }

    async _loadSubtitles(){
        await this.videoController.subtitle.loadSubtitles(this._content,this._basePlayer?.playerType);
        const tracks=this.videoController.subtitle.tracks.map(track=>track.lang);
        this.hub.dispatch('subtitleList',getLangDisplayList(tracks));
    }

    selectQuality(val){
        this._basePlayer.selectQuality?.(val);
    }

    selectAudio(val){
        this._basePlayer.selectAudio?.(val);
    }

    selectSubtitle(val){
        let chosenTrack=this.videoController.subtitle.tracks.find(sub=>sub.lang===val);
        if(chosenTrack)this.videoController.subtitle.addTrack(chosenTrack);
        else this.videoController.subtitle.removeTrack();
        //this._basePlayer.selectSubtitle?.(val);
    }

    getPlayerSettings(){
        return {...this._settings}
    }
    
    unloadContent(){
        if( !this._playbackActive ) return ;
        console.log('unloading............');
        this._playbackActive=false;
        this.videoController.subtitle.removeTrack();
        return this._basePlayer.unload();
    }

    destroy(){
        this._videoTag=null;
        this.active=false;
        this.hub.destroy();
        this.videoController.destroy();

        return this._basePlayer?.destroy()??Promise.resolve();
    }

    /** Methods to be Overridden: */ 
    _playerCreatorMap(playerType){
          switch(playerType){
            case 'dashPlayer':return ()=>import('@player/src/players/dashPlayer.js');
            case 'hlsPlayer':return ()=>import('@player/src/players/hlsPlayer.js');
            case 'html5Player':return ()=>import('@player/src/players/html5Player.js');
            default:return Promise.reject('content type not supported.');
          }  
    }


} 




 


export default Mainplayer;